import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUser, FiClock, FiCoffee, FiZap } = FiIcons;

function CustomerQueue({ customers, currentOrder, onSelectOrder }) {
  const getMoodColor = (mood) => {
    switch (mood) {
      case 'happy': return 'from-green-400 to-green-500';
      case 'neutral': return 'from-blue-400 to-blue-500';
      case 'impatient': return 'from-red-400 to-red-500';
      default: return 'from-gray-400 to-gray-500';
    }
  };

  const getMoodEmoji = (mood) => {
    switch (mood) {
      case 'happy': return '😊';
      case 'neutral': return '😐';
      case 'impatient': return '😤';
      default: return '🙂';
    }
  };

  const getDrinkEmoji = (drink) => {
    const drinkEmojis = {
      espresso: '☕',
      americano: '☕',
      latte: '🥛',
      cappuccino: '☕',
      macchiato: '☕',
      mocha: '🍫',
      matcha_tea: '🍃',
      matcha_latte: '🍵',
      matcha_frappuccino: '🧊'
    };
    return drinkEmojis[drink] || '☕';
  };

  const getPatienceLevel = (patience, maxPatience) => {
    const ratio = patience / maxPatience;
    if (ratio > 0.6) return { color: 'bg-green-400', label: 'Calm', icon: '😌' };
    if (ratio > 0.3) return { color: 'bg-yellow-400', label: 'Waiting', icon: '⏰' };
    return { color: 'bg-red-400', label: 'Urgent', icon: '🔥' };
  };

  return (
    <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl h-full flex flex-col">
      {/* Enhanced Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full flex items-center justify-center mr-3">
              <SafeIcon icon={FiUser} className="text-white text-lg" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800">Customer Queue</h3>
              <p className="text-xs text-gray-500">Tap to select an order</p>
            </div>
          </div>
          
          <div className="text-right">
            <div className="bg-amber-100 text-amber-600 px-3 py-1 rounded-full text-sm font-bold">
              {customers.length}/4
            </div>
            <p className="text-xs text-gray-500 mt-1">Active orders</p>
          </div>
        </div>
      </div>

      {/* Customer List - Improved scrolling */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        <AnimatePresence mode="popLayout">
          {customers.map((customer, index) => {
            const isSelected = currentOrder?.id === customer.id;
            const patienceLevel = getPatienceLevel(customer.patience, customer.maxPatience);
            
            return (
              <motion.div
                key={customer.id}
                className={`relative overflow-hidden rounded-2xl shadow-md cursor-pointer transition-all duration-200 ${
                  isSelected 
                    ? 'ring-3 ring-amber-400 bg-amber-50 border-2 border-amber-200' 
                    : 'bg-white hover:bg-gray-50 hover:shadow-lg'
                }`}
                layout
                initial={{ opacity: 0, x: -20, scale: 0.95 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 20, scale: 0.95 }}
                transition={{ 
                  type: "spring", 
                  damping: 25, 
                  stiffness: 500,
                  delay: index * 0.05 
                }}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => onSelectOrder(customer)}
              >
                {/* Priority Indicator */}
                {customer.patience <= 10 && (
                  <div className="absolute top-2 right-2 z-10">
                    <div className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                      <SafeIcon icon={FiZap} className="text-xs" />
                      URGENT
                    </div>
                  </div>
                )}

                <div className="p-4">
                  {/* Customer Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${getMoodColor(customer.mood)} flex items-center justify-center text-white font-bold text-lg mr-3 shadow-md`}>
                        <span className="text-xl">{getMoodEmoji(customer.mood)}</span>
                      </div>
                      <div>
                        <p className="font-bold text-gray-800 text-lg">{customer.name}</p>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            customer.mood === 'happy' ? 'bg-green-100 text-green-700' :
                            customer.mood === 'neutral' ? 'bg-blue-100 text-blue-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {customer.mood}
                          </span>
                          <span className="text-xs text-gray-500">
                            #{customer.id.toString().slice(-4)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="flex items-center text-sm text-gray-600 mb-1">
                        <SafeIcon icon={FiClock} className="mr-1" />
                        <span className="font-medium">{customer.patience}s</span>
                      </div>
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                        patienceLevel.color === 'bg-green-400' ? 'bg-green-100 text-green-700' :
                        patienceLevel.color === 'bg-yellow-400' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {patienceLevel.icon} {patienceLevel.label}
                      </div>
                    </div>
                  </div>

                  {/* Enhanced Patience Bar */}
                  <div className="mb-4">
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                      <motion.div 
                        className={`h-3 rounded-full transition-all duration-1000 ${patienceLevel.color} relative overflow-hidden`}
                        style={{ width: `${(customer.patience / customer.maxPatience) * 100}%` }}
                        animate={customer.patience <= 10 ? {
                          boxShadow: ['0 0 0 0 rgba(239, 68, 68, 0.7)', '0 0 0 10px rgba(239, 68, 68, 0)']
                        } : {}}
                        transition={{ duration: 1, repeat: Infinity }}
                      >
                        {customer.patience <= 10 && (
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse" />
                        )}
                      </motion.div>
                    </div>
                  </div>

                  {/* Order Details - Enhanced */}
                  <div className="bg-gray-50 rounded-xl p-3">
                    <div className="flex items-center mb-2">
                      <span className="text-2xl mr-3">{getDrinkEmoji(customer.drink)}</span>
                      <div className="flex-1">
                        <span className="font-semibold capitalize text-gray-800 text-base">
                          {customer.drink.replace('_', ' ')}
                        </span>
                        {customer.drink.includes('matcha') && (
                          <span className="ml-2 bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium">
                            🍃 Matcha
                          </span>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-green-600">$5</div>
                        <div className="text-xs text-gray-500">Base price</div>
                      </div>
                    </div>

                    {customer.extras.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {customer.extras.map((extra, idx) => (
                          <span key={idx} className="bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-xs font-medium">
                            + {extra.replace('_', ' ')}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Selection Indicator */}
                  {isSelected && (
                    <motion.div 
                      className="mt-3 text-center"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <div className="bg-amber-500 text-white px-4 py-2 rounded-full text-sm font-bold flex items-center justify-center gap-2">
                        <SafeIcon icon={FiCoffee} className="text-sm" />
                        Currently Making
                      </div>
                    </motion.div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>

        {/* Empty State - Enhanced */}
        {customers.length === 0 && (
          <motion.div 
            className="text-center py-12 px-4"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiCoffee} className="text-3xl text-gray-300" />
            </div>
            <h3 className="text-lg font-bold text-gray-400 mb-2">No Orders Yet</h3>
            <p className="text-gray-500 text-sm">
              Customers will start arriving soon.<br />
              Get ready to brew some amazing coffee!
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default CustomerQueue;